import { HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import {
  CONTEXT_RETURN_URL_QUERY_PARAM,
  LOGIN_REASON_QUERY_PARAM,
  normalizeContextReturnUrl,
  SESSION_EXPIRED_LOGIN_REASON,
} from '../platform/context-return-url';
import { PlatformContextService } from '../platform/platform-context.service';
import { AuthService } from './auth.service';
import { API_BASE_URL } from './api-base-url.token';

export { API_BASE_URL } from './api-base-url.token';
export const TENANT_HEADER_NAME = 'X-Tenant-Id';
export const EMPRESA_HEADER_NAME = 'X-Empresa-Id';
export const SUCURSAL_HEADER_NAME = 'X-Sucursal-Id';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const apiBaseUrl = inject(API_BASE_URL);
  if (!isApiRequest(request.url, apiBaseUrl)) {
    return next(request);
  }

  const authService = inject(AuthService);
  const platformContext = inject(PlatformContextService);
  const router = inject(Router);
  return from(authService.getAccessToken()).pipe(
    switchMap((accessToken) => {
      const headers: Record<string, string> = {};
      if (accessToken !== null) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const selectedTenant = platformContext.selectedTenant();
      const authMeRequest = isEndpoint(request.url, apiBaseUrl, '/auth/me');
      if (selectedTenant !== null && !authMeRequest) {
        headers[TENANT_HEADER_NAME] = String(selectedTenant.id);
      }

      const selectedCompany = platformContext.selectedCompany();
      const selectedBranch = platformContext.selectedBranch();
      const accessDiscoveryRequest = isEndpoint(request.url, apiBaseUrl, '/context/access');
      if (
        selectedTenant !== null &&
        selectedCompany !== null &&
        selectedBranch !== null &&
        !authMeRequest &&
        !accessDiscoveryRequest
      ) {
        headers[EMPRESA_HEADER_NAME] = String(selectedCompany.id);
        headers[SUCURSAL_HEADER_NAME] = String(selectedBranch.id);
      }

      return next(
        Object.keys(headers).length === 0 ? request : request.clone({ setHeaders: headers }),
      );
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        const shouldHandleUnauthorized = authService.shouldHandleUnauthorizedResponse();
        if (shouldHandleUnauthorized) {
          void authService.invalidateSession();
        }
        if (shouldHandleUnauthorized && !router.url.startsWith('/login')) {
          void router.navigate(['/login'], {
            queryParams: {
              [LOGIN_REASON_QUERY_PARAM]: SESSION_EXPIRED_LOGIN_REASON,
              [CONTEXT_RETURN_URL_QUERY_PARAM]: normalizeContextReturnUrl(router.url),
            },
            replaceUrl: true,
          });
        }
      }
      return throwError(() => error);
    }),
  );
};

function isApiRequest(requestUrl: string, apiBaseUrl: string): boolean {
  try {
    const request = new URL(requestUrl);
    const api = new URL(apiBaseUrl);
    const apiPath = api.pathname.replace(/\/$/, '');
    return (
      request.origin === api.origin &&
      (request.pathname === apiPath || request.pathname.startsWith(`${apiPath}/`))
    );
  } catch {
    return false;
  }
}

function isEndpoint(requestUrl: string, apiBaseUrl: string, endpointPath: string): boolean {
  try {
    const request = new URL(requestUrl);
    const api = new URL(apiBaseUrl);
    const apiPath = api.pathname.replace(/\/$/, '');
    return request.pathname === `${apiPath}${endpointPath}`;
  } catch {
    return false;
  }
}
